import {
  areObjectIdsEqual,
  getObjectIdMapValue,
  hasObjectIdMapValue,
} from 'helpers/idComparison';
import { isStageChangeChangelogOperation } from 'shared/domain/changelog/changelogGuards';
import { OperationV2Dto } from 'shared/types/changelogV2';
import { OperationV1 } from 'views/issue/issueHistory/types';

export type StageEntryDateMaps = {
  firstEntryDates: Map<string, string>;
  lastEntryDates: Map<string, string>;
};

function issueStartedOnFirstStage(
  stageChanges: OperationV2Dto[],
  firstStageId: string
): boolean {
  if (stageChanges.length === 0) {
    return true;
  }

  const firstChange = stageChanges[0];
  return (
    firstChange.stageIdBefore !== null &&
    areObjectIdsEqual(firstChange.stageIdBefore, firstStageId)
  );
}

export type ExtractStageEntryDatesOptions = {
  issueCreatedAt?: string;
  firstStageId?: string;
};

/** First- and last-entry timestamps per workflow stage from issue changelog. */
export function extractStageEntryDateMaps(
  operations: (OperationV1 | OperationV2Dto)[],
  options?: ExtractStageEntryDatesOptions
): StageEntryDateMaps {
  const stageChanges = operations
    .filter(isStageChangeChangelogOperation)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  const firstEntryDates = new Map<string, string>();
  const lastEntryDates = new Map<string, string>();

  for (const operation of stageChanges) {
    const stageId = operation.stageIdAfter!;
    if (!hasObjectIdMapValue(firstEntryDates, stageId)) {
      firstEntryDates.set(stageId, operation.createdAt);
    }
    lastEntryDates.set(stageId, operation.createdAt);
  }

  const { issueCreatedAt, firstStageId } = options ?? {};
  if (
    issueCreatedAt &&
    firstStageId &&
    !hasObjectIdMapValue(firstEntryDates, firstStageId) &&
    issueStartedOnFirstStage(stageChanges, firstStageId)
  ) {
    firstEntryDates.set(firstStageId, issueCreatedAt);
    if (!hasObjectIdMapValue(lastEntryDates, firstStageId)) {
      lastEntryDates.set(firstStageId, issueCreatedAt);
    }
  }

  return { firstEntryDates, lastEntryDates };
}

/** @deprecated Use extractStageEntryDateMaps — kept for callers needing first-entry only. */
export function extractStageEntryDates(
  operations: (OperationV1 | OperationV2Dto)[],
  options?: ExtractStageEntryDatesOptions
): Map<string, string> {
  return extractStageEntryDateMaps(operations, options).firstEntryDates;
}

export function getStageEntryDate(
  dateMaps: StageEntryDateMaps,
  stageId: string | undefined,
  isCurrentStage: boolean
): string | undefined {
  if (!stageId) {
    return undefined;
  }

  const dateMap = isCurrentStage
    ? dateMaps.lastEntryDates
    : dateMaps.firstEntryDates;
  return getObjectIdMapValue(dateMap, stageId);
}
